import React, {useEffect} from 'react';
import {FacilityRoleDto, UserInvitationDto} from 'server-openapi';
import {Button} from '../../../../kit/Button';
import {SelectOption} from '../../../../kit/Forms/Select';
import {useFieldArray} from 'react-hook-form';
import {UserDetailsFormValues, FacilityRoleAccessDto} from '../UserDetailsPage';
import styled from 'styled-components';
import {Layout} from '../../../../kit/Layout';
import {UseFormMethods} from 'react-hook-form';
import {useGroupPermissions} from "../../../../core/authz/PermissionsProvider";
import { UserFacilityRolesDetails } from "./UserFacilityRolesDetails";

export function FacilityRolesDetailsSection(props: {
  user: UserInvitationDto;
  facilityRoles: FacilityRoleDto[];
  form: UseFormMethods<UserDetailsFormValues>;
  facilityGroupId: number;
}) {
  const {form} = props;


  return (
    <>
      <HeaderText>Roles Details</HeaderText>
      <FacilityRoleFormOptionsDropdown facilityRoles={props.facilityRoles} form={form} facilityGroupId={props.facilityGroupId}/>
    </>
  );
}

interface IFacilityRoleFormOptionsDropdown {
  facilityRoles: FacilityRoleDto[];
  form: UseFormMethods<UserDetailsFormValues>;
  facilityGroupId: number;
}

function FacilityRoleFormOptionsDropdown(props: IFacilityRoleFormOptionsDropdown) {
    const {facilityRoles, form} = props;
    const groupPermissions = useGroupPermissions();
    const corporateOptionsVisible = groupPermissions.canManageCorporateUser;

    const allOptionsVisible = groupPermissions.canAssignRolesWithUserManagementPermission;
    const nonManagementOptionsVisible = groupPermissions.canAssignRolesExcludingUserManagementPermission;


    const basicUserManagementRolesOptionsVisible = groupPermissions.canAssignBasicUserManagementRoles;
    const registeredNurseRolesOptionsVisible = groupPermissions.canAssignRegisteredNurseRoles;
    const receptionistRolesOptionsVisible = groupPermissions.canAssignReceptionistRoles;

    const canAssignRoles = allOptionsVisible || nonManagementOptionsVisible || basicUserManagementRolesOptionsVisible || registeredNurseRolesOptionsVisible;
    const corporateLabels = ['Corporate Reporting'];
    const nonManagementLabels = ['Carer', 'Agency Nurse', 'Agency EN', 'Enrolled Nurse', 'Second Checker', 'Med Comp Carer', 'Med Assist', 'Disability Support Worker'];
    const basicUserManagementRoleLabels = ['Basic User Management'];
    const registeredNurseRoleLabels = ['Registered Nurse'];
    const receptionistRoleLabels = ['Reporting', 'Read Only'];

    const facilityRoleCanBeEdited = (r: FacilityRoleDto): boolean => {
        const roleName = r.name ?? '';
        return !!(corporateOptionsVisible ||
        (allOptionsVisible && !corporateLabels.includes(roleName)) ||
        (nonManagementOptionsVisible && nonManagementLabels.includes(roleName)) ||
        (basicUserManagementRolesOptionsVisible && basicUserManagementRoleLabels.includes(roleName)) ||
        (registeredNurseRolesOptionsVisible && registeredNurseRoleLabels.includes(roleName)) ||
        (receptionistRolesOptionsVisible && receptionistRoleLabels.includes(roleName)));

    }

    const {fields, append} = useFieldArray<FacilityRoleAccessDto>({
        name: 'facilityRoleAccess',
        control: form.control,
    });

    useEffect(() => {
        if (fields.length === 0) {
            append({startDate: new Date(), isNew: true, active: true});
        }
        form.control.shouldUnregister = true; // entering value into Designation field removes newly added role field (created by clicking on +Add Additional Role button). This prevents the issue.
    }, []);

  return (
    <SectionInputsContainer gap={1} padding="20px">
      {fields.map((field, index) => {
        if (field.facilityRole) {
          const isFieldDisabled =
            !corporateOptionsVisible &&
            !!field.facilityRole &&
            field.facilityRole?.name !== undefined &&
            field.facilityRole?.name !== null && !facilityRoleCanBeEdited(field.facilityRole);

          const facilityRoleOptions = facilityRoles
            .sort((a, b) => a.name!.localeCompare(b.name!))
            .filter((r) => facilityRoleCanBeEdited(r) || r.name === field.facilityRole?.name)
            .map((r) => ({label: r.name, value: r} as SelectOption<FacilityRoleDto>));
          return (
            <UserFacilityRolesDetails
              key={field.id}
              field={field}
              index={index}
              form={form}
              facilityRoleOptions={facilityRoleOptions}
              disabled={isFieldDisabled}
              roleDisabled={!field?.isNew}
            />
          );
        }
      })
      }
      <Button
        onClick={() => {
          append({
            startDate: new Date(),
            isNew: true,
            active: true,
            expiryDate: undefined,
            facilityRole: {name: null} //, id: 0}
          });
        }}
        disabled={!canAssignRoles}
      >
        + Add {fields.length > 0 ? 'additional ' : ''} role
      </Button>
    </SectionInputsContainer>
  );
}

const HeaderText = styled.h2`
    margin-left: 20px;
`;

const SectionInputsContainer = styled(Layout)`
    background: ${(p) => p.theme.backgrounds.lighter.bg};
    margin-bottom: 10em;
`;
